import type {FindListFunc} from "../../PartInjectKeys";
import type {Ref} from "vue";
import {ref} from "vue";
import {cast} from "../../editors/Editor";
import { Icon } from "anneca-symbols";
import type {
	CellFormatter, ComponentCell,
	DateFormatter, FormatterInput, HasFormatterInput,
	IconFormatter,
	JoinFormatter,
	ListFormatter,
	MoneyFormatter, SequenceFormatter
} from "@t/fd/layout/Agenda";
import {escapeHtml, SafeHtml} from "@t/util/SafeHtml";
import {parseDate} from "@t/util/DateUtil";
import type {FormList} from "@t/fd/layout/FormDefinition";
import {CellComponent} from "@fd/containers/agenda/AgendaUtils.ts";

export function formatterOf(f:CellFormatter|ComponentCell):FormatterBase {
    switch (f.type) {
		case "app": return new CellComponentFormatter(f as ComponentCell);
        case "join": return new Join(f as JoinFormatter);
        case "money": return new Money(f as MoneyFormatter);
        case "date": return new Temporal(f as DateFormatter);
        case "icon": return new IconFmt(f as IconFormatter);
        case "list": return new List(f as ListFormatter);
        case "map": return new List(f as ListFormatter, false);
    }
    throw "Invalid formatter type "+f.type;
}

export abstract class FormatterBase {
	protected in: FormatterInput[] | undefined;
	protected args: any[] | null;

	constructor(f:HasFormatterInput) {
		this.in = f.in;
		this.args = null;
	}

	/**
	 * Evaluate input arguments for this formatter
	 */
	arguments(data:Map<string, any>):any[] {
		if (this.args == null) {
			const args = Array.isArray(this.in) ? this.in : ["$0"];
			this.args = [];
			for (let fi of args) {
				let val: any;
				if (typeof fi === "string") {
					// This is variable name
					val = data.get(fi);
				} else {
					// This is input from another formatter
					val = formatterOf(fi).format(data);
				}
				this.args.push(val === undefined ? null : val);
			}
		}
		return this.args;
	}

	/**
	 * Get Nth argument
	 * @param data  all values extracted
	 * @param idx   index of argument
	 */
	argument(data: Map<string, any>, idx: number): any {
		return this.arguments(data)[idx];
	}

	hasArgument(data: Map<string, any>, idx:number):boolean {
		const vals = this.arguments(data);
		return vals.length > idx;
	}

	abstract process(data:Map<string, any>):void;
	abstract format(data:Map<string, any>):any;
}


export class HtmlFormatterBase extends FormatterBase {
    private out: string | undefined;

    constructor(f:CellFormatter) {
		super(f);
        this.out = f.out;
    }

    output(data:Map<string, any>, result:any):void {
        data.set(this.out ? this.out : "value", result);
    }

    process(data:Map<string, any>) {
    }

    format(data:Map<string, any>):any {
        this.process(data);
        return data.get(this.out ? this.out : "value");
    }
}

export class CellComponentFormatter extends FormatterBase {
	constructor(f:ComponentCell) {
		super(f);
	}

	output(data:Map<string, any>, result:any):void {
		data.set("html", result);
	}

	process(data:Map<string, any>) {
		// Only evaluate
		const out = new SafeHtml();
		for (let value of this.arguments(data)) {
			out.append(value);
		}
		this.output(data, out);
	}

	format(data:Map<string, any>):any {
		this.process(data);
		return data.get("html");
	}
}


class Join extends HtmlFormatterBase {
    private separator: string;
    constructor(f: JoinFormatter) {
        super(f);
        this.separator = f.separator == null ? " " : f.separator;
    }

    process(data: Map<string, any>) {
        const out = new SafeHtml();
        let idx = 0;
        for (let value of this.arguments(data)) {
            out.append(value);
            if (idx > 0) out.appendHtml(this.separator);
            idx++;
        }
        this.output(data, out);
    }
}

class Money extends HtmlFormatterBase {
    private options: object;
    constructor(f: MoneyFormatter) {
        super(f);
        this.options = f.options ? f.options : {};
    }

    process(data: Map<string, any>) {
        const ops:any = {
            ...this.options
        };
        const amount = this.argument(data, 0);
        if (this.hasArgument(data,1)) {
            const currency = this.argument(data, 1);
            ops.style = "currency";
            ops.currency = currency;
        }
        const result = new Intl.NumberFormat(navigator.language, ops).format(amount);
        this.output(data, result);
    }
}

const DATE_OPS:Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric"
};

const DATE_TIME_OPS:Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "numeric",
    weekday: "short"
};

class Temporal extends HtmlFormatterBase {
    private options: object;
    constructor(f: DateFormatter) {
        super(f);
        this.options = f.options ? f.options : {};
    }

    process(data: Map<string, any>) {
        const value = this.argument(data, 0);
        let result:string;
        if (value == null) result = "";
        else {
            if (typeof value === "string") {
                const parsed = parseDate(value);
                if (parsed.date == null) result = "";
                else {
                    let ops;
                    if (parsed.wholeDay) {
                        ops = {
                            ...DATE_OPS,
                            ...this.options
                        }
                    } else {
                        ops = {
                            ...DATE_TIME_OPS,
                            ...this.options
                        }
                    }
                    result = new Intl.DateTimeFormat(navigator.language, ops).format(parsed.date);
                }
            } else if (value instanceof Date) {
                result = new Intl.DateTimeFormat(navigator.language, { ...DATE_OPS, ...this.options }).format(value);
            } else result = "";
        }

        if (result.length > 0) result = "<span class='nw'>"+result+"</span>";

        this.output(data, result);
    }
}

class Sequence extends HtmlFormatterBase {
    private sequence: CellFormatter[];
    constructor(f: SequenceFormatter) {
        super(f);
        this.sequence = f.seq;
    }

    process(data: Map<string, any>) {
        let idx = 0;
        let value:any = null;
        for (let cf of this.sequence) {
            const f = formatterOf(cf);
            if (idx === this.sequence.length - 1) {
                value = f.format(data)
            } else f.process(data);
            idx++;
        }
        if (idx > 0) this.output(data, value);
    }
}


class List extends HtmlFormatterBase {
    private list: string | FormList;
    private ifmissing: string | undefined;
    private escape: boolean;

    constructor(f: ListFormatter, escapeOutput = true) {
        super(f);
        this.list = f.list;
        this.ifmissing = f.ifmissing;
        this.escape = escapeOutput;
    }


    process(data: Map<string, any>) {
        let listArray:Ref<FormList>;
        if (Array.isArray(this.list)) listArray = ref(this.list);
        else {
            const findList = data.get("$findList") as FindListFunc;
            listArray = findList(this.list);
        }
        let value = this.argument(data, 0);
        if (value == null) value = "";
        else if (typeof value !== "string") {
            // list support only string values
            value = cast("string", value);
        }
        let li = listArray.value.find((item) => item.value === value);
        if (!li) {
            if (this.ifmissing !== undefined) {
                li = { value: value, name: this.ifmissing };
            } else {
                li = { value: value, name: "("+value+")" };
            }
        }
        this.output(data, this.escape ? escapeHtml(li.name) : li.name);
    }
}

class IconFmt extends HtmlFormatterBase {
    constructor(f: IconFormatter) {
        super(f);
    }

    process(data: Map<string, any>) {
        const icon = this.argument(data, 0);
        const res = new SafeHtml(Icon.fi(icon));
        this.output(data, res);
    }
}